import {
  adjectives,
  animals,
  colors,
  uniqueNamesGenerator,
} from 'unique-names-generator';

/**
 * F18 — Tenant-discovery helpers (pure-ish; one `fetch` call boundary).
 *
 * Two surfaces:
 *   - `probeTenantDomain(domain, fetcher?)` — looks up a tenant's GUID
 *     via Microsoft's well-known OpenID Connect config endpoint. No
 *     auth required; the endpoint is public per Microsoft Identity
 *     Platform docs.
 *   - `parseTenantDisplayName(orgResponse)` — pure extractor for the
 *     Graph `/v1.0/organization` shape.
 *
 * The `fetcher` arg lets unit tests inject a stub. In production we
 * pass `fetch.bind(window)`.
 */

const WELL_KNOWN_BASE = 'https://login.microsoftonline.com';

export interface TenantProbeOk {
  kind: 'ok';
  tenantId: string;
  issuer: string;
  /** OIDC `tenant_region_scope` field — useful for routing telemetry. */
  region: string | null;
}

export interface TenantProbeNotFound {
  kind: 'not-found';
  /** Whether the request itself succeeded (we got an HTTP response) or
   *  failed at the network layer. */
  reason: 'http-404' | 'network-error' | 'malformed-response';
  message: string;
}

export type TenantProbeResult = TenantProbeOk | TenantProbeNotFound;

/**
 * Resolve a tenant's `tenantId` from its primary domain by hitting the
 * OIDC well-known config endpoint. Public endpoint — no auth needed.
 *
 * Example:
 *   probeTenantDomain('microsoft.com') →
 *     { kind: 'ok', tenantId: '72f988bf-...', issuer: 'https://...', region: 'WW' }
 */
export async function probeTenantDomain(
  domain: string,
  fetcher: typeof fetch = fetch,
): Promise<TenantProbeResult> {
  const trimmed = domain.trim().toLowerCase();
  if (trimmed.length === 0) {
    return {
      kind: 'not-found',
      reason: 'malformed-response',
      message: 'domain is empty',
    };
  }
  if (!/^[a-z0-9.-]+$/.test(trimmed)) {
    return {
      kind: 'not-found',
      reason: 'malformed-response',
      message: 'domain has invalid characters',
    };
  }

  const url = `${WELL_KNOWN_BASE}/${trimmed}/v2.0/.well-known/openid-configuration`;

  let res: Response;
  try {
    res = await fetcher(url, { method: 'GET' });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { kind: 'not-found', reason: 'network-error', message };
  }

  if (res.status === 404 || res.status === 400) {
    return {
      kind: 'not-found',
      reason: 'http-404',
      message: `Microsoft returned ${res.status} for domain ${trimmed}`,
    };
  }
  if (!res.ok) {
    return {
      kind: 'not-found',
      reason: 'http-404',
      message: `Microsoft returned ${res.status}`,
    };
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { kind: 'not-found', reason: 'malformed-response', message };
  }

  if (!isOidcConfig(body)) {
    return {
      kind: 'not-found',
      reason: 'malformed-response',
      message: 'response did not match OIDC config shape',
    };
  }

  // `issuer` is `https://login.microsoftonline.com/<tenantId>/v2.0`.
  const tenantId = extractTenantIdFromIssuer(body.issuer);
  if (!tenantId) {
    return {
      kind: 'not-found',
      reason: 'malformed-response',
      message: 'could not parse tenantId from issuer',
    };
  }

  return {
    kind: 'ok',
    tenantId,
    issuer: body.issuer,
    region: body.tenant_region_scope ?? null,
  };
}

interface OidcConfig {
  issuer: string;
  tenant_region_scope?: string;
}

function isOidcConfig(v: unknown): v is OidcConfig {
  if (typeof v !== 'object' || v === null) return false;
  const obj = v as Record<string, unknown>;
  return typeof obj.issuer === 'string';
}

/** Pull the tenant GUID out of the OIDC issuer URL. */
export function extractTenantIdFromIssuer(issuer: string): string | null {
  // Shape: https://login.microsoftonline.com/<tenantId>/v2.0
  const match = issuer.match(
    /^https:\/\/login\.microsoftonline\.com\/([0-9a-f-]{36})\//,
  );
  return match?.[1] ?? null;
}

/**
 * Pull the display name from a Microsoft Graph `/v1.0/organization`
 * response. Pure — no fetch. Returns null when the response shape is
 * unexpected (caller decides whether to retry).
 */
export function parseTenantDisplayName(orgResponse: unknown): string | null {
  if (typeof orgResponse !== 'object' || orgResponse === null) return null;
  const obj = orgResponse as Record<string, unknown>;
  // The endpoint returns `{ value: [{ displayName: '...' }] }`.
  if (!Array.isArray(obj.value) || obj.value.length === 0) return null;
  const first = obj.value[0];
  if (typeof first !== 'object' || first === null) return null;
  const name = (first as Record<string, unknown>).displayName;
  return typeof name === 'string' && name.length > 0 ? name : null;
}

/**
 * F18.demo.5 — Pre-fill source ranking from Clerk identity.
 *
 * If the user signed into Clerk via Microsoft OAuth, their Microsoft
 * identity is sitting in Clerk's `externalAccounts` array — that's a
 * MUCH stronger signal than guessing from the primary-email domain.
 * Scan for a Microsoft external account first; only fall through to
 * the primary-email heuristic if there isn't one.
 *
 * `verification.status === 'verified'` filters out unverified accounts
 * that Clerk may track but haven't been confirmed via OAuth round-trip.
 *
 * Input shape narrowly types the subset of Clerk's user object we
 * need — `useUser()` returns a fully-typed user, but we accept a
 * structural type here so the helper stays pure + easy to test.
 */
export interface ClerkExternalAccount {
  provider: string;
  emailAddress?: string | null;
  verification?: { status?: string | null } | null;
}

export function deriveTenantDomainFromMicrosoftAccount(
  externalAccounts: ReadonlyArray<ClerkExternalAccount> | null | undefined,
): { email: string; domain: string } | null {
  if (!externalAccounts || externalAccounts.length === 0) return null;
  // Clerk's Microsoft OAuth provider key is 'oauth_microsoft'. The
  // older 'microsoft' shorthand appears in some Clerk SDK versions —
  // match both to be defensive.
  const microsoftAccounts = externalAccounts.filter(
    (a) => a.provider === 'oauth_microsoft' || a.provider === 'microsoft',
  );
  if (microsoftAccounts.length === 0) return null;
  // Prefer verified accounts; if none verified, accept the first
  // anyway (Clerk's verification can lag the OAuth callback).
  const verified = microsoftAccounts.find(
    (a) => a.verification?.status === 'verified',
  );
  const best = verified ?? microsoftAccounts[0]!;
  const email = (best.emailAddress ?? '').trim().toLowerCase();
  if (!email) return null;
  const atIdx = email.lastIndexOf('@');
  if (atIdx < 1 || atIdx === email.length - 1) return null;
  const domain = email.slice(atIdx + 1);
  // Personal-email Microsoft accounts (outlook.com, hotmail.com,
  // live.com) are NOT organization tenants — they're consumer MSA.
  // Filter those out so the wizard doesn't try to provision into a
  // consumer "tenant".
  const consumer = new Set([
    'outlook.com',
    'hotmail.com',
    'live.com',
    'msn.com',
    'passport.com',
  ]);
  if (consumer.has(domain)) return null;
  return { email, domain };
}

/**
 * Extract the primary domain from an email address. Returns the part
 * after the `@`, lowercased. Returns null if the email is malformed or
 * uses a personal-email provider where the domain is NOT the user's
 * organization (gmail, hotmail, outlook, etc.) — for those cases we
 * leave the wizard's tenant field empty so the user types their real
 * firm domain.
 */
export function deriveTenantDomainFromEmail(email: string): string | null {
  const trimmed = email.trim().toLowerCase();
  const atIdx = trimmed.lastIndexOf('@');
  if (atIdx < 1 || atIdx === trimmed.length - 1) return null;
  const domain = trimmed.slice(atIdx + 1);
  // Reject obvious personal-email providers — these aren't the user's
  // organization tenant. The wizard's tenant input stays empty so the
  // user types their actual firm domain.
  const personal = new Set([
    'gmail.com',
    'googlemail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'live.com',
    'icloud.com',
    'me.com',
    'aol.com',
    'protonmail.com',
    'proton.me',
  ]);
  if (personal.has(domain)) return null;
  return domain;
}

/**
 * Best-guess firm name from a tenant domain. Strips TLD + capitalizes.
 * Example: 'morrison-legal.com' → 'Morrison Legal'. Returns empty
 * string for empty input.
 */
export function deriveFirmNameFromDomain(domain: string): string {
  const trimmed = domain.trim().toLowerCase();
  if (!trimmed) return '';
  // Drop everything from the last `.` onward (TLD).
  const dotIdx = trimmed.lastIndexOf('.');
  const root = dotIdx > 0 ? trimmed.slice(0, dotIdx) : trimmed;
  // Replace separators with spaces, title-case each word.
  return root
    .replace(/[-_]+/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * F18.demo — Build a default portal slug for AI-lawyer-beta users.
 *
 * Uses `unique-names-generator` with the Clerk userId as a stable
 * seed → three-word slugs like `quiet-amber-falcon`. Properties:
 *   - Deterministic: same seed → same slug. Reloads keep the same
 *     slug, so resume-the-wizard always matches the existing draft.
 *   - Globally unique per user: two users named "John Smith" get
 *     different slugs because their Clerk userIds differ.
 *   - No PII: user's name and email never appear in the slug, so
 *     legal portal URLs aren't doxxing their owners.
 *   - No Math.random: the previous 3-char base36 suffix collided
 *     (only 46k possibilities) and re-rolled on every render.
 *
 * Pass the Clerk `user.id` as `seed` in production. Tests can pass
 * any string for deterministic fixtures.
 */
export function deriveSlugFromUser(args: { seed: string }): string {
  const seed = args.seed;
  if (typeof seed !== 'string' || seed.length === 0) {
    throw new Error('deriveSlugFromUser: seed required (use Clerk user.id)');
  }
  return uniqueNamesGenerator({
    dictionaries: [adjectives, colors, animals],
    separator: '-',
    style: 'lowerCase',
    length: 3,
    seed,
  });
}

/**
 * F18.demo — Best-guess firm name from Clerk identity. Same arg shape
 * as deriveSlugFromUser so callers can derive both consistently.
 */
export function deriveFirmNameFromUser(args: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}): string {
  const fn = (args.firstName ?? '').trim();
  const ln = (args.lastName ?? '').trim();
  if (fn || ln) {
    return `${fn} ${ln}`.trim();
  }
  const email = (args.email ?? '').trim();
  const atIdx = email.indexOf('@');
  if (atIdx > 0) {
    const local = email.slice(0, atIdx);
    // Title-case the local part: "duncan.david" → "Duncan David".
    return local
      .replace(/[._-]+/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 0)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return '';
}

/**
 * Slug derivation: turns "Morrison Legal & Associates" into "morrison-legal".
 * Pure, deterministic.
 */
export function deriveSlugFromFirmName(firmName: string): string {
  return firmName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]+/g, '') // drop punctuation
    .replace(/\s+/g, '-')           // spaces → hyphens
    .replace(/-+/g, '-')            // collapse consecutive hyphens
    .replace(/^-|-$/g, '')          // trim leading/trailing hyphens
    .slice(0, 32);                  // cap at 32 chars (matches existing portal slug rules)
}
